import React, { useEffect, useState, useCallback } from 'react';
import adminApi from '../api/client';
import MarketplaceReports from '../components/MarketplaceReports';
import MarketplaceContactSettings from '../components/MarketplaceContactSettings';

interface MarketplaceStats {
  total_listings: number;
  active_listings: number;
  total_orders: number;
  pending_orders: number;
  revenue_total: number;
}

interface Listing {
  id: string;
  title: string;
  price_lkr: number;
  condition: string;
  city: string;
  status: string;
  created_at: string;
  seller_id: string;
  seller_name: string;
  stock_quantity: number;
}

interface ListingsResponse {
  total: number;
  page: number;
  limit: number;
  listings: Listing[];
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          toast-enter cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
      </div>
    ))}
  </div>
);

const ConfirmDialog: React.FC<{
  open: boolean; title: string; message: string; confirmLabel: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}> = ({ open, title, message, confirmLabel, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 bg-[#D85A30] hover:bg-[#D85A30]/90 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {loading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'active') return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-500"/> Active</span>;
  if (normalized === 'pending') return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500"/> Pending</span>;
  if (normalized === 'removed') return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-500"/> Removed</span>;
  return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">{status}</span>;
}

const ConditionBadge: React.FC<{ condition: string }> = ({ condition }) => {
  return (
    <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
      {condition}
    </span>
  );
};

const Marketplace: React.FC = () => {
  const [stats, setStats] = useState<MarketplaceStats | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('All');
  
  const [toasts, setToasts] = useState<Toast[]>([]);
  let toastId = React.useRef(0);

  const [confirmOpts, setConfirmOpts] = useState<{ open: boolean, id: string, title: string }>({ open: false, id: '', title: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const perPage = 20;
  const loadGeneration = React.useRef(0);

  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => { setToasts((prev) => prev.filter((t) => t.id !== id)); }, 4000);
  }, []);

  const fetchData = useCallback(async () => {
    const request = ++loadGeneration.current;
    try {
      setLoading(true);
      const [statsRes, listRes] = await Promise.all([
        adminApi.get<MarketplaceStats>('/admin/marketplace/stats'),
        adminApi.get<ListingsResponse>('/admin/marketplace/listings', {
          params: {
            page: page,
            limit: perPage,
            status: statusFilter === 'All' ? undefined : statusFilter.toLowerCase()
          }
        })
      ]);
      if (request !== loadGeneration.current) return;
      setStats(statsRes.data);
      setListings(listRes.data.listings);
      setTotal(listRes.data.total);
    } catch (error) {
      if (request === loadGeneration.current) addToast('Failed to load marketplace data.', 'error');
    } finally {
      if (request === loadGeneration.current) setLoading(false);
    }
  }, [page, statusFilter, addToast]);

  const latestFetch = React.useRef(fetchData);
  useEffect(() => {
    latestFetch.current = fetchData;
    void fetchData();
    return () => { loadGeneration.current += 1; };
  }, [fetchData]);
  // A pending moderation action must refresh the view selected at completion.
  const refreshCurrentView = useCallback(() => { void latestFetch.current(); }, []);

  const removeListing = async () => {
    if (!confirmOpts.id) return;
    try {
      setActionLoading(true);
      await adminApi.post(`/admin/marketplace/listings/${confirmOpts.id}/remove`);
      addToast('Listing removed successfully.', 'success');
      setConfirmOpts({ open: false, id: '', title: '' });
      refreshCurrentView();
    } catch (error: any) {
       addToast(error.response?.data?.detail || 'Failed to remove listing.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Marketplace Oversight</h1>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 border-l-4 border-[#085041] shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Listings</p>
          <p className="text-3xl font-bold text-gray-800">
             {loading ? <span className="animate-pulse bg-gray-200 rounded w-16 h-8 inline-block"/> : (stats?.total_listings ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 border-l-4 border-[#1D9E75] shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Active Listings</p>
          <p className="text-3xl font-bold text-gray-800">
             {loading ? <span className="animate-pulse bg-gray-200 rounded w-16 h-8 inline-block"/> : (stats?.active_listings ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 border-l-4 border-[#BA7517] shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Orders</p>
          <p className="text-3xl font-bold text-gray-800">
             {loading ? <span className="animate-pulse bg-gray-200 rounded w-16 h-8 inline-block"/> : (stats?.total_orders ?? 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 border-l-4 border-[#D85A30] shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Revenue</p>
          <p className="text-3xl font-bold text-gray-800">
             {loading ? <span className="animate-pulse bg-gray-200 rounded w-24 h-8 inline-block"/> : `LKR ${(stats?.revenue_total ?? 0).toLocaleString()}`}
          </p>
        </div>
      </div>

      <MarketplaceContactSettings />
      <MarketplaceReports onListingChanged={refreshCurrentView} />
      {/* Table Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-8">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <h2 className="text-lg font-bold text-gray-800">Listings Database</h2>
          
          <div className="flex items-center gap-3">
             <label className="text-sm font-medium text-gray-600">Status:</label>
             <select 
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="bg-white border border-gray-200 text-sm rounded-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
             >
                <option value="All">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Removed">Removed</option>
             </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Title', 'Seller', 'City', 'Price', 'Condition', 'Status', 'Actions'].map(col => (
                  <th key={col} className="px-5 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{col}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                     {Array.from({length: 7}).map((_, j) => <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-full"/></td>)}
                  </tr>
                ))
              ) : listings.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-gray-400 font-medium">No listings found.</td></tr>
              ) : (
                listings.map(l => (
                  <tr key={l.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4 text-sm font-semibold text-gray-800 truncate max-w-[200px]">{l.title}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{l.seller_name}</td>
                    <td className="px-5 py-4 text-sm text-gray-600">{l.city || '—'}</td>
                    <td className="px-5 py-4 text-sm font-bold text-gray-800">LKR {(l.price_lkr ?? 0).toLocaleString()}</td>
                    <td className="px-5 py-4"><ConditionBadge condition={l.condition} /></td>
                    <td className="px-5 py-4"><StatusBadge status={l.status} /></td>
                    <td className="px-5 py-4">
                      {l.status?.toLowerCase() !== 'removed' && (
                        <button
                          onClick={() => setConfirmOpts({ open: true, id: l.id, title: l.title })}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#D85A30]/10 text-[#D85A30] hover:bg-[#D85A30]/20 transition-colors border border-[#D85A30]/20"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
           <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
              <span className="text-sm text-gray-500">
                 Showing <span className="font-semibold text-gray-700">{(page - 1) * perPage + 1}</span>–
                 <span className="font-semibold text-gray-700">{Math.min(page * perPage, total)}</span> of <span className="font-semibold text-gray-700">{total}</span>
              </span>
              <div className="flex items-center gap-2">
                 <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Prev</button>
                 <span className="text-sm px-3"><span className="font-bold text-[#085041]">{page}</span> / <span className="text-gray-500">{totalPages}</span></span>
                 <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
              </div>
           </div>
        )}
      </div>

      <ToastContainer toasts={toasts} onDismiss={(id) => setToasts(p => p.filter(t => t.id !== id))} />
      <ConfirmDialog
        open={confirmOpts.open}
        title="Remove Listing"
        message={`Are you sure you want to completely remove "${confirmOpts.title}" from the marketplace? This action cannot be easily undone.`}
        confirmLabel="Remove"
        loading={actionLoading}
        onConfirm={removeListing}
        onCancel={() => setConfirmOpts({ open: false, id: '', title: '' })}
      />
    </div>
  );
};

export default Marketplace;
